import React, {
  useEffect,
  useMemo,
} from 'react';
import {
  Button,
} from '@populateui/ui';
import {
  notEmptyString,
} from '@populateui/utils';
import debounce from 'lodash/debounce';
import isNil from 'lodash/isNil';
import Skeleton from 'core/components/Skeleton';
import {
  DEFAULT_EMPTY_VALUE,
} from 'core/constants';
import {
  PCCCommuniqueUpdatedNotificationDTO,
  PCCDashboardActivityDTO,
  PCCPatientDetailResponseDTO,
} from 'dtos';
import PCCAlertList from 'pages/Dashboard/pages/Dashboard/PCCOverview/alerts/PCCAlertList';
import {
  PCCPatientActionContext,
} from 'pages/Dashboard/pages/Dashboard/PCCOverview/types';
import {
  DateRange,
} from 'types/dateRange';
import {
  formatDateTime,
  formatMonth,
} from 'utils/date';
import useSignalREffect from 'views/EMR/hooks/signalR';

type Props = {
  data?: PCCPatientDetailResponseDTO;
  dateRange: DateRange;
  isError: boolean;
  isFetching: boolean;
  onRefetch: () => void;
  patient: PCCPatientActionContext;
};

type ActivitySkeletonProps = {
  cardCount?: number;
};

type ErrorStateProps = {
  onRetry: () => void;
};

type ActivityRowProps = {
  activity: PCCDashboardActivityDTO;
  isLast: boolean;
};

function ActivitySkeleton({ cardCount = 3 }: ActivitySkeletonProps) {
  return (
    <div className="flex flex-col gap-3">
      <div className="rounded-lg border border-gray-200 bg-white p-3">
        <div className="flex items-center justify-between gap-3">
          <Skeleton variant="rounded" className="!h-4 !w-32" />
          <Skeleton variant="rounded" className="!h-7 !w-28" />
        </div>
      </div>
      {Array.from({ length: cardCount }, (_, cardIndex) => (
        <div
          key={`activity-skeleton-${cardIndex}`}
          className="rounded-lg border border-gray-200 bg-white p-3"
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Skeleton variant="rounded" className="!h-2.5 !w-2.5 !rounded-full" />
              <Skeleton variant="rounded" className="!h-4 !w-20" />
            </div>
            <Skeleton variant="rounded" className="!h-6 !w-20" />
          </div>
          <div className="mt-3 flex flex-col gap-2">
            <Skeleton variant="rounded" className="!h-4 !w-40" />
            <Skeleton variant="rounded" className="!h-10 !w-full" />
          </div>
        </div>
      ))}
      <div className="rounded-lg border border-gray-200 bg-white p-3">
        <Skeleton variant="rounded" className="!h-3 !w-24" />
        <div className="mt-3 flex flex-col gap-2">
          <Skeleton variant="rounded" className="!h-10 !w-full" />
          <Skeleton variant="rounded" className="!h-10 !w-full" />
        </div>
      </div>
    </div>
  );
}

function ErrorState({ onRetry }: ErrorStateProps) {
  return (
    <div className="flex h-40 flex-col items-center justify-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 text-sm text-red-600">
      <span>Unable to load patient activity.</span>
      <Button
        type="button"
        variant="outlined"
        color="primary"
        size="small"
        onClick={onRetry}
      >
        Retry
      </Button>
    </div>
  );
}

function ActivityRow({ activity, isLast }: ActivityRowProps) {
  const includedPrograms = activity.includedPrograms?.join(' · ') ?? '';
  const programLabel = notEmptyString(includedPrograms)
    ? includedPrograms
    : DEFAULT_EMPTY_VALUE;
  const title = notEmptyString(activity.title)
    ? activity.title
    : DEFAULT_EMPTY_VALUE;

  return (
    <div className="flex gap-3">
      <div className="flex shrink-0 flex-col items-center">
        <span className="mt-1 h-2 w-2 rounded-full border-2 border-gray-400 bg-white" />
        {!isLast && <div className="w-px flex-1 bg-gray-200" />}
      </div>
      <div className="min-w-0 flex-1 pb-3">
        <div className="text-xs font-semibold text-gray-800">
          {programLabel}
          {' · '}
          {title}
        </div>
        <div className="text-[11px] text-gray-600">
          {notEmptyString(activity.occurredOn)
            ? formatDateTime(activity.occurredOn ?? '')
            : DEFAULT_EMPTY_VALUE}
        </div>
      </div>
    </div>
  );
}

export default function PatientActivityContent({
  data,
  dateRange,
  isError,
  isFetching,
  onRefetch,
  patient,
}: Props) {
  const recentActivity = data?.recentActivity ?? [];
  const isInitialLoading = isFetching && isNil(data);
  const showContent = !isInitialLoading && !isError;
  const showRecentActivity = showContent && recentActivity.length > 0;
  const showEmptyActivity = showContent && recentActivity.length === 0;

  const debouncedRefetch = useMemo(
    () => debounce(onRefetch, 100),
    [onRefetch],
  );

  useEffect(() => () => {
    debouncedRefetch.cancel();
  }, [debouncedRefetch]);

  useSignalREffect('OnPCCCommuniqueUpdated', ({ patientId }: PCCCommuniqueUpdatedNotificationDTO) => {
    if (patientId === patient.patientId) {
      debouncedRefetch();
    }
  }, [debouncedRefetch, patient.patientId]);

  return (
    <div className="min-h-0 flex-1 overflow-y-auto bg-gray-50 p-4">
      {isInitialLoading && <ActivitySkeleton />}
      {!isInitialLoading && isError && (
        <ErrorState onRetry={onRefetch} />
      )}
      {showContent && (
        <div className="flex flex-col gap-3">
          <PCCAlertList
            alerts={data?.alerts}
            patient={patient}
          />
          {showRecentActivity && (
            <section className="rounded-lg border border-gray-200 bg-white p-3">
              <h5 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                Activity Log
              </h5>
              <div className="mt-2">
                {recentActivity.map((activity, activityIndex) => (
                  <ActivityRow
                    key={`${activity.includedPrograms?.join(' · ') ?? 'activity'}-${activity.title ?? ''}-${activity.occurredOn ?? activityIndex}`}
                    activity={activity}
                    isLast={activityIndex === recentActivity.length - 1}
                  />
                ))}
              </div>
            </section>
          )}
          {showEmptyActivity && (
            <div className="rounded-lg border border-gray-200 bg-white p-5 text-center text-xs text-gray-500">
              No activity in {formatMonth(dateRange.min)}.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
